import { openDB } from 'idb';
import { logger } from '@/utils/logger';

const DB_NAME = 'flexhelp-offline';
const DB_VERSION = 1;

export async function initializeOfflineSupport() {
  try {
    const db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Store for cached API responses
        if (!db.objectStoreNames.contains('api-cache')) {
          db.createObjectStore('api-cache', { keyPath: 'url' });
        }

        // Store for offline data
        if (!db.objectStoreNames.contains('offline-data')) {
          db.createObjectStore('offline-data', { keyPath: 'id' });
        }

        // Store for pending actions
        if (!db.objectStoreNames.contains('pending-actions')) {
          const store = db.createObjectStore('pending-actions', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          store.createIndex('status', 'status');
          store.createIndex('timestamp', 'timestamp');
        }
      },
    });

    // Set up online/offline event handlers
    window.addEventListener('online', () => {
      logger.info('Application is online');
      processPendingActions().catch(logger.error);
    });

    window.addEventListener('offline', () => {
      logger.info('Application is offline');
    });

    // Register service worker if supported
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          type: 'module',
        });
        logger.info('Service Worker registered:', registration);
      } catch (error) {
        logger.error('Service Worker registration failed:', error);
      }
    }

    logger.info('Offline support initialized');
    return db;
  } catch (error) {
    logger.error('Failed to initialize offline support:', error);
    throw error;
  }
}

// Rest of the file remains unchanged
export async function cacheApiResponse(url: string, response: any) {
  const db = await openDB(DB_NAME);
  await db.put('api-cache', {
    url,
    data: response,
    timestamp: Date.now(),
  });
}

export async function getCachedApiResponse(url: string) {
  const db = await openDB(DB_NAME);
  return db.get('api-cache', url);
}

export async function saveOfflineData(key: string, data: any) {
  const db = await openDB(DB_NAME);
  await db.put('offline-data', {
    id: key,
    data,
    timestamp: Date.now(),
  });
}

export async function getOfflineData(key: string) {
  const db = await openDB(DB_NAME);
  return db.get('offline-data', key);
}

export async function queueAction(action: {
  type: string;
  payload: any;
  meta?: any;
}) {
  const db = await openDB(DB_NAME);
  await db.add('pending-actions', {
    ...action,
    status: 'pending',
    timestamp: Date.now(),
    retryCount: 0,
  });
}

async function processPendingActions() {
  const db = await openDB(DB_NAME);
  const tx = db.transaction('pending-actions', 'readwrite');
  const store = tx.objectStore('pending-actions');
  
  const pendingActions = await store.index('status').getAll('pending');
  
  for (const action of pendingActions) {
    try {
      await processAction(action);
      await store.put({
        ...action,
        status: 'completed',
        completedAt: Date.now(),
      });
    } catch (error) {
      logger.error('Failed to process action:', error);
      if (action.retryCount >= 3) {
        await store.put({
          ...action,
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      } else {
        await store.put({
          ...action,
          retryCount: (action.retryCount || 0) + 1,
        });
      }
    }
  }
}

async function processAction(action: any) {
  logger.debug('Processing action:', action);
}

export function isOfflineMode(): boolean {
  return !navigator.onLine;
}